import React, { Component, Dispatch } from 'react';
import { connect } from 'react-redux';
import { SofaButton } from '@sofology/sofologycomponents';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';

import './CheckoutAuthentication.scss';

import { Loader } from 'components';
import { LoginForm, RegisterForm } from '../';
import { generateDispatchToPropsMap, generateForgotPasswordUrl, logsHelper, errorToBasketErrorModel, scrollHelper, analyticsHelper } from 'helpers';
import { AppState } from 'store';
import {
    clearEmail,
    ClearEmailFunc,
    clearLoginAuthErrors,
    ClearLoginAuthErrorsFunc,
    loginProcess,
    LoginProcessFunc,
    registerProcess,
    RegisterProcessFunc
} from 'store/checkout/actions';
import { SofaFormComponent } from 'interfaces';
import { LoginFormType } from '../LoginForm/LoginForm';
import { RegisterFormType } from '../RegisterForm/RegisterForm';
import { FORGOT_PASSWORD_HINT } from '../DeliveryDetailsForm/deliveryDetailsAdditionalData';

enum RenderMode {
    Login = 'login',
    Register = 'register'
}

export interface LoginFormData {
    email: string;
    password: string;
}

export interface RegisterFormData {
    firstName: string;
    lastName: string;
    email: string;
    confirmEmail: string;
    password: string;
    confirmPassword: string;
}

interface OwnProps {
    pathname: string;
}

interface OwnPropsWithDefault {
    loginFormData: LoginFormData;
    registerFormData: RegisterFormData;
}

interface OwnState {
    loginInputState: LoginFormData;
    registerInputState: RegisterFormData;
    mode: RenderMode;
    isAuthClickProcessing: boolean;
}

interface StateProps {
    isCheckingEmail: boolean;
    isAuthProcess: boolean;
    hasAuthErrors: boolean;
    basketId: string;
}

interface DispatchProps {
    loginProcess: LoginProcessFunc;
    registerProcess: RegisterProcessFunc;
    clearLoginAuthErrors: ClearLoginAuthErrorsFunc;
    clearEmail: ClearEmailFunc;
}

class CheckoutAuthentication extends Component<OwnProps & OwnPropsWithDefault & StateProps & DispatchProps, OwnState> {
    static defaultProps: Partial<OwnPropsWithDefault> = {
        loginFormData: {
            email: '',
            password: ''
        },
        registerFormData: {
            firstName: '',
            lastName: '',
            email: '',
            confirmEmail: '',
            password: '',
            confirmPassword: ''
        }
    };

    private loginFormElement = React.createRef<LoginFormType>();
    private registerFormElement = React.createRef<RegisterFormType>();
    private authFormElement = React.createRef<HTMLDivElement>();

    constructor(props: OwnProps & OwnPropsWithDefault & StateProps & DispatchProps) {
        super(props);

        this.state = {
            loginInputState: this.props.loginFormData,
            registerInputState: this.props.registerFormData,
            mode: RenderMode.Login,
            isAuthClickProcessing: false
        };
    }

    componentDidUpdate(prevProps: Readonly<OwnProps & StateProps & DispatchProps>): void {
        if (this.state.mode === RenderMode.Login && this.state.isAuthClickProcessing && !this.props.isAuthProcess && prevProps.isAuthProcess) {
            this.setState({ isAuthClickProcessing: false }, () => {
                this.validateLoginForm();
            });
        }

        if (this.state.mode === RenderMode.Register && this.state.isAuthClickProcessing && !this.props.isAuthProcess && prevProps.isAuthProcess) {
            this.setState({ isAuthClickProcessing: false }, () => {
                this.validateRegisterForm();
            });
        }
    }

    handleLoginClick = () => {
        if (this.state.isAuthClickProcessing === false && this.loginInputNotEmpty()) {
            this.setState({ isAuthClickProcessing: true }, () => {
                this.validateLoginForm()
                    .then(isValid => {
                        if (isValid) {
                            this.props.clearLoginAuthErrors();

                            analyticsHelper.sendToGoogleLoginMessage(this.props.pathname, 'user-login-submit');

                            const formComponent = this.loginFormElement.current! as SofaFormComponent<LoginFormData>;
                            const values = formComponent.getFormData();
                            this.props.loginProcess(values.email, values.password, this.props.pathname);
                        } else {
                            this.setState({ isAuthClickProcessing: false });
                        }
                    })
                    .catch(error => {
                        logsHelper.error(errorToBasketErrorModel(error, 'CheckoutAuthentication:handleLoginClick'));
                        this.setState({ isAuthClickProcessing: false });
                    });
            });
        } else {
            this.validateLoginForm();
        }

        this.memorizeLoginInput();
    };

    handleRegisterClick = () => {
        if (this.state.isAuthClickProcessing) {
            return;
        }

        const form = this.registerFormElement.current;
        if (!form) {
            return;
        }

        this.setState({ isAuthClickProcessing: true }, () => {
            this.validateRegisterForm()
                .then(isValid => {
                    if (isValid) {
                        const values = this.registerFormElement.current?.getFormData();

                        if (!values) {
                            return;
                        }

                        this.props.registerProcess(this.props.pathname, values.email, values.password, values.firstName, values.lastName);
                    } else {
                        this.setState({ isAuthClickProcessing: false });
                    }
                })
                .catch(error => {
                    logsHelper.error(errorToBasketErrorModel(error, 'CheckoutAuthentication:handleRegisterClick'));
                    this.setState({ isAuthClickProcessing: false });
                });
        });

        this.memorizeRegistrationInput();
    };

    render(): JSX.Element {
        const { isCheckingEmail, isAuthProcess } = this.props;
        const shouldShowLoader = isCheckingEmail || isAuthProcess;

        return (
            <div className='checkout-authentication' ref={this.authFormElement}>
                {shouldShowLoader && <Loader show={shouldShowLoader} overlay={true} />}
                {this.state.mode === RenderMode.Login && this.renderLogin()}
                {this.state.mode === RenderMode.Register && this.renderRegister()}
            </div>
        );
    }

    renderLogin(): JSX.Element {

        return (
            <div className='auth-layout auth-layout--login'>
                <div className='auth-layout__left'>
                    <div className='auth-layout__login-title'>
                        Log in to your Sofology account
                    </div>
                    <LoginForm
                        ref={this.loginFormElement}
                        formData={this.state.loginInputState}
                        goToRegistrationPage={this.goToRegistrationPage(true)}
                        hasAuthErrors={this.props.hasAuthErrors}
                    />
                    {this.props.hasAuthErrors && this.state.mode === RenderMode.Login && (
                        <div className='auth-layout__error'>
                            There's been an error signing into your account. Please try again later or call <a className='auth-layout__error-link' href='tel:03444818181'>03444 81 81 81</a> to speak to a sofologist
                        </div>
                    )}
                    <div className='login-actions'>
                        {this.renderForgotPasswordLink()}
                        <SofaButton text='Log in' variant='primary-rounded' onClick={this.handleLoginClick} />
                    </div>
                </div>
                <div className='auth-layout__divider' />
                <div className='auth-layout__right'>
                    <div className='auth-layout__right-inner'>
                        <div className='auth-layout__register-title'>
                            Don't have a Sofology account?
                        </div>
                        <SofaButton
                            text='Create an account'
                            variant='primary-rounded'
                            onClick={this.goToRegistrationPage(false)}
                        />
                    </div>
                </div>
            </div>
        );
    }

    renderRegister(): JSX.Element {
        return (
            <div className='auth-layout auth-layout--register'>
                <div className='auth-layout__back'>
                    <button className='register-back-btn' onClick={this.goToLoginPage(false)}>
                        <ArrowBackIosIcon />
                        Back
                    </button>
                </div>
                <div className='auth-layout__register-page-title'>Register your Sofology account</div>
                <RegisterForm
                    ref={this.registerFormElement}
                    formData={this.state.registerInputState}
                    goToLoginPage={this.goToLoginPage(true)}
                    hasAuthErrors={this.props.hasAuthErrors}
                />
                {this.props.hasAuthErrors && this.state.mode === RenderMode.Register && (
                    <div className='auth-layout__error'>
                        There was a problem creating your account. Please try again later or call <a className='auth-layout__error-link' href='tel:03444818181'>03444 81 81 81</a> to speak to a sofologist
                    </div>
                )}
                <div className='checkout-authentication__register-btn'>
                    <SofaButton text='Create account' variant='primary-rounded' onClick={this.handleRegisterClick} />
                </div>
            </div>
        );
    }

    handleForgotPasswordClick = (): void => {
        analyticsHelper.sendToGoogleLoginMessage(
            this.props.pathname,
            'login-forgot-password'
        );
    };

    renderForgotPasswordLink = () => (
        <a
            className='login-actions__forgot'
            href={generateForgotPasswordUrl(this.props.basketId)}
            onClick={this.handleForgotPasswordClick}
        >
            {FORGOT_PASSWORD_HINT}
        </a>
    );

    loginInputNotEmpty(): boolean {
        const formData = this.loginFormElement.current?.getFormData();
        if (!formData) {
            return false;
        }

        return formData.email !== '' && formData.password !== '';
    }

    memorizeRegistrationInput = () => {
        const formData = this.registerFormElement.current?.getFormData();
        if (!formData) {
            return;
        }

        const {
            firstName = '',
            lastName = '',
            email = '',
            confirmEmail = '',
            password = '',
            confirmPassword = '',
        } = formData;

        this.setState({
            registerInputState: {
                firstName,
                lastName,
                email,
                confirmEmail,
                password,
                confirmPassword
            }
        });
    };

    memorizeLoginInput = () => {
        const formData = this.loginFormElement.current?.getFormData();
        if (!formData) {
            return;
        }

        const { email = '', password = '' } = formData;

        this.setState({
            loginInputState: {
                email,
                password
            }
        });
    };

    goToLoginPage = (loginWithExistingEmail: boolean) => (): void => {
        this.memorizeRegistrationInput();
        this.props.clearLoginAuthErrors();

        if (loginWithExistingEmail) {
            this.setState({
                loginInputState: {
                    email: this.registerFormElement.current?.getFormData().email ?? '',
                    password: ''
                }
            });
        } else {
            this.props.clearEmail();
        }

        this.setState({ mode: RenderMode.Login });

        scrollHelper.scrollTo(this.authFormElement);
    };

    goToRegistrationPage = (registerWithEmail: boolean) => (): void => {
        const { pathname } = this.props;
        analyticsHelper.sendToGoogleLoginMessage(pathname, 'user-register-start');

        this.memorizeLoginInput();
        this.props.clearLoginAuthErrors();

        if (registerWithEmail) {
            this.setState({
                registerInputState: {
                    firstName: '',
                    lastName: '',
                    email: this.loginFormElement.current?.getFormData().email ?? '',
                    confirmEmail: '',
                    password: '',
                    confirmPassword: ''
                }
            });
        } else {
            this.props.clearEmail();
        }

        this.setState({ mode: RenderMode.Register });

        scrollHelper.scrollTo(this.authFormElement);
    };

    async validateLoginForm(): Promise<boolean> {
        let isValid = true;
        let loginFormInvalidControl = await this.loginFormElement.current?.validate() ?? [];

        if (loginFormInvalidControl.length > 0) {
            isValid = false;
            let firstInvalidControl = loginFormInvalidControl.min(c => c.index);
            if (firstInvalidControl.ref?.current) {
                scrollHelper.scrollTo(firstInvalidControl.ref?.current.element);
            }
        }

        return isValid;
    }

    async validateRegisterForm(): Promise<boolean> {
        let isValid = true;
        let registerFormInvalidControls = await this.registerFormElement.current?.validate() ?? [];

        if (registerFormInvalidControls.length > 0) {
            isValid = false;

            let firstInvalidControl = registerFormInvalidControls.min(c => c.index);
            if (firstInvalidControl.ref?.current) {
                scrollHelper.scrollTo(firstInvalidControl.ref?.current.element);
            }
        }

        return isValid;
    }
}

function mapStateToProps(state: AppState): StateProps {
    return {
        isCheckingEmail: state.checkoutState.isCheckingEmail,
        isAuthProcess: state.checkoutState.isAuthProcess,
        hasAuthErrors: Boolean(state.checkoutState.authErrors),
        basketId: state.basketState.basket!.guid
    };
}

function mapDispatchToProps(dispatch: Dispatch<any>): DispatchProps {
    return generateDispatchToPropsMap(dispatch, [loginProcess, registerProcess, clearEmail, clearLoginAuthErrors]);
}

export default connect<StateProps, DispatchProps, OwnProps & OwnPropsWithDefault, AppState>(
    mapStateToProps,
    mapDispatchToProps
)(CheckoutAuthentication);